import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { parseLocalDate } from '../../utils/formatters';
import { chartPalette } from './chartPalette';

export interface ProductTrendChartData {
  productKeys: string[];
  productLabels: string[];
  data: Array<{ date: string; unitsByProduct: Record<string, number> }>;
}

type DashboardMode = 'light' | 'dark';

export interface ProductTrendChartProps {
  data: ProductTrendChartData;
  loading?: boolean;
  mode?: DashboardMode;
}

const ChartContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode?: DashboardMode }>(({ mode = 'dark' }) => {
  const isLight = mode === 'light';
  return {
    padding: '24px',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    ...(isLight
      ? {
          background: '#ffffff',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          '&::before': { content: 'none' },
        }
      : {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
  };
});

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode?: DashboardMode }>(({ mode = 'dark' }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: mode === 'light' ? '#0f172a' : '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.01em',
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
}));

export const ProductTrendChart: React.FC<ProductTrendChartProps> = ({
  data: rawData,
  loading = false,
  mode = 'dark',
}) => {
  const isLight = mode === 'light';
  const gridStroke = isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.05)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)';
  const tickFill = isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)';

  const {
    productKeys,
    productLabels,
    data: dataPoints,
  } = rawData ?? {
    productKeys: [],
    productLabels: [],
    data: [],
  };

  const chartData = useMemo(() => {
    return dataPoints.map((point) => {
      const row: Record<string, string | number> = {
        date: point.date,
        dateLabel: point.date ? format(parseLocalDate(point.date), 'd MMM', { locale: es }) : '',
      };
      productKeys.forEach((key) => {
        row[key] = point.unitsByProduct?.[key] ?? 0;
      });
      return row;
    });
  }, [dataPoints, productKeys]);

  const productTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    productKeys.forEach((k) => {
      totals[k] = 0;
    });
    dataPoints.forEach((point) => {
      productKeys.forEach((k) => {
        totals[k] = (totals[k] ?? 0) + (point.unitsByProduct?.[k] ?? 0);
      });
    });
    return totals;
  }, [dataPoints, productKeys]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: Record<string, unknown>; color: string; name: string }>;
  }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload as Record<string, unknown>;
    const dateLabel = String(point.dateLabel ?? point.date ?? '');
    return (
      <Box
        sx={
          isLight
            ? {
                background: '#ffffff',
                border: '1px solid #e0f2fe',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }
        }
      >
        <Typography
          sx={{ fontSize: '12px', fontWeight: 600, color: isLight ? '#64748b' : '#ffffff', mb: 1 }}
        >
          {dateLabel}
        </Typography>
        {productKeys.map((key, i) => {
          const val = (point[key] as number) ?? 0;
          const color = chartPalette.series[i % chartPalette.series.length];
          const label = productLabels[i] ?? `Producto ${key}`;
          return (
            <Typography key={key} sx={{ fontSize: '13px', color, fontWeight: 600 }}>
              {label}: {val.toLocaleString('es-BO')} uds.
            </Typography>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <ChartContainer mode={mode}>
        <Box
          sx={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            Cargando...
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  if (!chartData?.length || !productKeys?.length) {
    return (
      <ChartContainer mode={mode}>
        <Title mode={mode}>Tendencia por producto</Title>
        <Box
          sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            No hay datos para los productos seleccionados
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer mode={mode}>
      <Title mode={mode}>Tendencia por producto</Title>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, position: 'relative', zIndex: 1 }}
      >
        {productKeys.map((key, i) => {
          const total = productTotals[key] ?? 0;
          const color = chartPalette.series[i % chartPalette.series.length];
          const label = productLabels[i] ?? `Producto ${key}`;
          return (
            <Box
              key={key}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                bgcolor: isLight ? 'grey.50' : 'rgba(255,255,255,0.05)',
                border: '1px solid',
                borderColor: isLight ? 'grey.200' : 'rgba(255,255,255,0.1)',
              }}
            >
              <Typography sx={{ fontSize: '11px', color: tickFill, fontWeight: 500 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color }}>
                {total.toLocaleString('es-BO')} uds.
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis
              dataKey="dateLabel"
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
            />
            <YAxis
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              tickFormatter={(v) => Number(v).toLocaleString('es-BO')}
              label={{
                value: 'Piezas',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fill: tickFill,
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ position: 'relative' }}
              formatter={(value) => (
                <span style={{ color: tickFill, fontSize: '12px' }}>{value}</span>
              )}
            />
            {productKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={productLabels[i] ?? `Producto ${key}`}
                stroke={chartPalette.series[i % chartPalette.series.length]}
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 1 }}
                activeDot={{ r: 5 }}
                animationDuration={800}
                animationEasing="ease-out"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </ChartContainer>
  );
};
